import { apiJson } from './client';

export const CARD_TAB_ACTIVITY_KEYS = [
  'notes',
  'sub-tasks',
  'role-messages',
  'debate',
  'sub-agents',
  'research',
  'calls',
  'members',
  'artifacts.files',
  'artifacts.links',
  'artifacts.voice',
  'artifacts.scheduled',
  'artifacts.kb',
] as const;

export type CardTabActivityKey = (typeof CARD_TAB_ACTIVITY_KEYS)[number];
export type CardTabIndicatorKey = CardTabActivityKey | 'artifacts';

export interface CardTabActivityState {
  count: number;
  activityVersion: number;
  latestActivityAt: string | null;
  seenThroughAt: string | null;
  seenThroughCount: number;
  unseen: boolean;
  live: boolean;
}

export interface CardTabActivityResponse {
  tabs: Record<CardTabIndicatorKey, CardTabActivityState>;
  roleMessagesVisible: boolean;
}

export interface CardTabMarkSeenResponse {
  tabKey: CardTabActivityKey;
  activity: CardTabActivityState;
}

const artifactLeafKeys: CardTabActivityKey[] = [
  'artifacts.files',
  'artifacts.links',
  'artifacts.voice',
  'artifacts.scheduled',
  'artifacts.kb',
];

function latestIso(states: CardTabActivityState[]): string | null {
  let latest: string | null = null;
  let latestMs = Number.NEGATIVE_INFINITY;
  for (const state of states) {
    if (!state.latestActivityAt) continue;
    const ms = Date.parse(state.latestActivityAt);
    if (Number.isFinite(ms) && ms > latestMs) {
      latestMs = ms;
      latest = new Date(ms).toISOString();
    }
  }
  return latest;
}

function aggregateArtifacts(
  tabs: CardTabActivityResponse['tabs'],
): CardTabActivityState {
  const states = artifactLeafKeys.map((key) => tabs[key]);
  const count = states.reduce((sum, state) => sum + state.count, 0);
  const activityVersion = states.reduce((sum, state) => sum + state.activityVersion, 0);
  const latestActivityAt = latestIso(states);
  const unseen = states.some((state) => state.unseen);
  return {
    count,
    activityVersion,
    latestActivityAt,
    seenThroughAt: count > 0 && !unseen ? latestActivityAt : null,
    seenThroughCount: count > 0 && !unseen ? activityVersion : 0,
    unseen,
    live: states.some((state) => state.live),
  };
}

/** Replace one leaf state and keep the derived Artifacts marker in sync. */
export function withCardTabActivity(
  current: CardTabActivityResponse,
  tabKey: CardTabActivityKey,
  activity: CardTabActivityState,
): CardTabActivityResponse {
  const tabs = { ...current.tabs, [tabKey]: activity };
  if (tabKey.startsWith('artifacts.')) {
    tabs.artifacts = aggregateArtifacts(tabs);
  }
  return { ...current, tabs };
}

/** Immediate red→gray cache transition while the durable PUT is in flight. */
export function optimisticallyMarkCardTabSeen(
  current: CardTabActivityResponse,
  tabKey: CardTabActivityKey,
  throughActivityAt: string,
  throughItemCount: number,
): CardTabActivityResponse {
  const previous = current.tabs[tabKey];
  const latestMs = previous.latestActivityAt
    ? Date.parse(previous.latestActivityAt)
    : Number.NaN;
  const throughMs = Date.parse(throughActivityAt);
  const unseen =
    previous.count > 0 &&
    Number.isFinite(latestMs) &&
    (!Number.isFinite(throughMs) ||
      latestMs > throughMs ||
      (latestMs === throughMs && previous.activityVersion > throughItemCount));
  return withCardTabActivity(current, tabKey, {
    ...previous,
    seenThroughAt: throughActivityAt,
    seenThroughCount: throughItemCount,
    unseen,
  });
}

export const cardTabActivityApi = {
  get(cardId: string): Promise<CardTabActivityResponse> {
    return apiJson<CardTabActivityResponse>(`/cards/${cardId}/tab-activity`);
  },
  markSeen(
    cardId: string,
    tabKey: CardTabActivityKey,
    throughActivityAt: string,
    throughItemCount: number,
  ): Promise<CardTabMarkSeenResponse> {
    return apiJson<CardTabMarkSeenResponse>(
      `/cards/${cardId}/tab-activity/${encodeURIComponent(tabKey)}/seen`,
      {
        method: 'PUT',
        json: { throughActivityAt, throughItemCount },
      },
    );
  },
};

export const cardTabActivityKeys = {
  all: ['card-tab-activity'] as const,
  forCard: (cardId: string) => [...cardTabActivityKeys.all, cardId] as const,
};
